import { useEffect, useRef } from 'react';
import type { Action, Item, Rotation } from '@qoobd/rules';
import { game } from '../game/controller';
import { useGame } from '../game/store';

const KEYS: Record<string, Action> = {
  ArrowLeft: { kind: 'rotate', rotation: 'turnLeft' },
  KeyA: { kind: 'rotate', rotation: 'turnLeft' },
  ArrowRight: { kind: 'rotate', rotation: 'turnRight' },
  KeyD: { kind: 'rotate', rotation: 'turnRight' },
  ArrowUp: { kind: 'rotate', rotation: 'pitchUp' },
  KeyW: { kind: 'rotate', rotation: 'pitchUp' },
  ArrowDown: { kind: 'rotate', rotation: 'pitchDown' },
  KeyS: { kind: 'rotate', rotation: 'pitchDown' },
  KeyQ: { kind: 'rotate', rotation: 'rollLeft' },
  KeyE: { kind: 'rotate', rotation: 'rollRight' },
  Space: { kind: 'step' },
  Enter: { kind: 'step' },
  NumpadEnter: { kind: 'step' },
  KeyR: { kind: 'emp' },
  KeyF: { kind: 'bulkhead' },
};
const SWAP_KEYS: Record<string, Item> = { Digit1: 'rock', Numpad1: 'rock', Digit2: 'paper', Numpad2: 'paper', Digit3: 'scissors', Numpad3: 'scissors' };

const rot = (rotation: Rotation): Action => ({ kind: 'rotate', rotation });

/** Keyboard, the on-screen pad, and swipe gestures on the 3D view. */
export function Controls() {
  const playing = useGame((s) => s.phase === 'playing');
  const gesturesOnly = useGame((s) => s.padMode === 'gestures');

  useEffect(() => {
    if (!playing) return;
    const onKey = (e: KeyboardEvent) => {
      const action = KEYS[e.code] ?? (SWAP_KEYS[e.code] ? ({ kind: 'swap', item: SWAP_KEYS[e.code] } as Action) : null);
      if (!action) return;
      if (e.target instanceof HTMLButtonElement) e.target.blur();
      e.preventDefault();
      if (e.repeat) return; // held keys never chain actions
      game.input(action);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playing]);

  return (
    <div className={`hud br${gesturesOnly ? ' gestures-only' : ''}`}>
      <div className="pad" role="group" aria-label="Movement controls">
        <PadButton label="Roll left" hint="Q" action={rot('rollLeft')} icon={<path d="M8 5a8 8 0 1 0 9 0M8 1v4H4" />} />
        <PadButton label="Pitch up" hint="W ↑" action={rot('pitchUp')} icon={<path d="M12 19V5M6 11l6-6 6 6" />} />
        <PadButton label="Roll right" hint="E" action={rot('rollRight')} icon={<path d="M16 5a8 8 0 1 1-9 0M16 1v4h4" />} />
        <PadButton label="Turn left" hint="A ←" action={rot('turnLeft')} icon={<path d="M19 12H5M11 6l-6 6 6 6" />} />
        <PadButton
          label="Step forward"
          hint="SPACE"
          action={{ kind: 'step' }}
          className="go"
          icon={
            <>
              <rect x="7" y="4" width="10" height="16" />
              <path d="M12 9v6M9.5 12.5 12 15l2.5-2.5" />
            </>
          }
        />
        <PadButton label="Turn right" hint="D →" action={rot('turnRight')} icon={<path d="M5 12h14M13 6l6 6-6 6" />} />
        <span />
        <PadButton label="Pitch down" hint="S ↓" action={rot('pitchDown')} icon={<path d="M12 5v14M6 13l6 6 6-6" />} />
        <span />
      </div>
    </div>
  );
}

function PadButton({ label, hint, action, icon, className }: { label: string; hint: string; action: Action; icon: React.ReactNode; className?: string }) {
  return (
    <button
      type="button"
      className={className}
      aria-label={label}
      onPointerDown={(e) => {
        e.preventDefault();
        game.input(action);
      }}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') e.preventDefault();
      }}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        {icon}
      </svg>
      <kbd>{hint}</kbd>
    </button>
  );
}

/**
 * Touch gestures on the 3D view: swipe left/right to turn, up/down to pitch,
 * a two-finger twist to roll, and a tap to step.
 */
export function useGestures(target: React.RefObject<HTMLElement>) {
  const start = useRef<{ x: number; y: number; t: number; angle: number | null; fingers: number } | null>(null);
  useEffect(() => {
    const el = target.current;
    if (!el) return;
    const angleOf = (e: TouchEvent) =>
      e.touches.length >= 2 ? Math.atan2(e.touches[1]!.clientY - e.touches[0]!.clientY, e.touches[1]!.clientX - e.touches[0]!.clientX) : null;
    const onStart = (e: TouchEvent) => {
      const t = e.touches[0]!;
      start.current = { x: t.clientX, y: t.clientY, t: performance.now(), angle: angleOf(e), fingers: e.touches.length };
    };
    const onMove = (e: TouchEvent) => {
      const s = start.current;
      if (!s || s.angle === null || e.touches.length < 2) return;
      let delta = angleOf(e)! - s.angle;
      if (delta > Math.PI) delta -= 2 * Math.PI;
      if (delta < -Math.PI) delta += 2 * Math.PI;
      if (Math.abs(delta) > 0.5) {
        game.input(rot(delta > 0 ? 'rollRight' : 'rollLeft'));
        start.current = null;
      }
    };
    const onEnd = (e: TouchEvent) => {
      const s = start.current;
      start.current = null;
      if (!s || s.fingers > 1) return;
      const t = e.changedTouches[0]!;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 12 && performance.now() - s.t < 300) game.input({ kind: 'step' });
      else if (dist >= 40) {
        if (Math.abs(dx) > Math.abs(dy)) game.input(rot(dx < 0 ? 'turnRight' : 'turnLeft'));
        else game.input(rot(dy < 0 ? 'pitchDown' : 'pitchUp'));
      }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
    };
  }, [target]);
}
